import { Level } from "../Level.js";

/**
 * The Village — an explorable safe town between dungeon levels (KayKit Medieval
 * Hexagon pack). The grass grid fills the whole map (no visible edge); only the
 * central area is walkable (an invisible border stops you), and a ring of trees
 * frames the town instead of a fence. Visit the Blacksmith, Innkeeper and
 * Merchant (E), then step through the gate portal to descend.
 */
export function generateHub(theme) {
  const W = 46, H = 36;
  const level = new Level(W, H, theme);

  // grass everywhere → no abyss / no visible boundary
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) level.setFloor(x, y, (x + y) % 2);

  // only the central rectangle is walkable (invisible wall = blocked margin)
  const wx0 = 10, wx1 = 35, wy0 = 7, wy1 = 28;
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++)
      if (x < wx0 || x > wx1 || y < wy0 || y > wy1) level.block[y][x] = 1;

  level.playerStart = { gx: 22, gy: 27 };   // town entrance
  level.exit = { gx: 33, gy: 8 };           // dungeon gate

  const put = (gx, gy, key, opts = {}) => level.addProp({
    gx: gx + 0.5, gy: gy + 0.5, shape: opts.shape || "decor", key, color: 0x808080,
    blocking: !!opts.blocking, scale: opts.scale || 1, rot: opts.rot || 0, fit: opts.fit,
  });

  // ---- buildings ----
  put(14, 12, "hex/blacksmith", { shape: "building", blocking: true, fit: 5, scale: 1.5 });
  put(22, 11, "hex/home", { shape: "building", blocking: true, fit: 5, scale: 1.5 });
  put(30, 12, "hex/shop", { shape: "building", blocking: true, fit: 5, scale: 1.5 });
  put(14, 22, "hex/church", { shape: "building", blocking: true, fit: 7, scale: 1.6 });

  // ---- yards / props ----  (scale here sizes the collision circle: radius ≈ 0.42 * scale)
  put(12, 15, "hex/weaponrack", { fit: 1.8, blocking: true, scale: 1.4 });
  put(16, 14, "hex/tent", { fit: 2.4, blocking: true, scale: 2.1 });
  put(13, 13, "hex/bucket", { fit: 0.9, blocking: true, scale: 0.9 });
  put(28, 15, "hex/crate", { fit: 1.3, blocking: true, scale: 1.4 });
  put(32, 14, "hex/barrel", { fit: 1.0, blocking: true, scale: 1.2 });
  put(29, 16, "hex/sack", { fit: 0.9, blocking: true, scale: 1.1 });
  put(22, 27, "hex/flag", { fit: 3, blocking: false, scale: 0.8 });   // marks the spawn / town entrance
  // a few rocks/trees inside the town
  for (const [rx, ry] of [[11, 10], [34, 18], [19, 25], [27, 9]]) put(rx, ry, Math.random() < 0.5 ? "hex/rock_a" : "hex/rock_c", { fit: 1.2, blocking: true, scale: 1.4 });
  for (const [tx, ty] of [[12, 26], [33, 26], [26, 24], [18, 9]]) put(tx, ty, Math.random() < 0.5 ? "hex/tree_a" : "hex/tree_b", { fit: 3, blocking: true, scale: 1.5 });

  // ---- forest ring framing the town (in the non-walkable grass margin) ----
  for (let x = 6; x <= 39; x += 2) { put(x, 4, tree(), { fit: 3.2, scale: 0.7 }); put(x, 31, tree(), { fit: 3.2, scale: 0.7 }); }
  for (let y = 4; y <= 31; y += 3) { put(7, y, tree(), { fit: 3.2, scale: 0.7 }); put(38, y, tree(), { fit: 3.2, scale: 0.7 }); }

  // ---- NPCs in front of their buildings ----
  level.npcs = [
    { type: "blacksmith", model: "characters/barbarian", name: "Grimr the Smith", gx: 14, gy: 15 },
    { type: "home", model: "characters/ranger", name: "Old Bram", gx: 22, gy: 14 },
    { type: "shop", model: "characters/mage", name: "Vessa the Merchant", gx: 30, gy: 15 },
  ];

  return level;
}

const tree = () => (Math.random() < 0.5 ? "hex/tree_a" : "hex/tree_b");
